import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCcw, CheckCircle2, Hourglass } from "lucide-react";
import { toast } from "sonner";
import Dots from "./Dots";
import CardDetails from "./CardDetails";
import ActionNotes from "./ActionNotes";
import ReminderControl from "./ReminderControl";
import { restoreAction, setActionStatus } from "../../lib/api";

const LABEL = { coaches_brief: "Coaches Brief", action_taken: "Action Taken" };

export function ActionCard({ card, onChanged }) {
  const id = card.id;
  const k = card.category.key;
  const values = useRef({ brief: card.coaches_brief, taken: card.action_taken });
  const onValues = useCallback((v) => { values.current = v; }, []);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const completed = card.status === "completed";

  const move = async (status) => {
    setError(""); setBusy(true);
    try {
      if (status === "restore") await restoreAction(id);
      else await setActionStatus(id, status, values.current.brief || "", values.current.taken || "");
      toast.success(status === "completed" ? "Marked Completed" : "Moved to In Process");
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.missing) setError(`Required before completing: ${d.missing.map((m) => LABEL[m]).join(" and ")}. ${d.message}`);
      else setError(typeof d === "string" ? d : "Couldn't update this action");
    } finally { setBusy(false); }
  };

  return (
    <article className="surface-card p-5 md:p-6" data-testid={`action-card-${id}`} data-category={k} data-status={card.status}>
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <Dots dots={card.category.dots} testid={`card-dots-${id}`} />
          <span className="font-serif text-2xl text-brand-ink" data-testid={`card-name-${id}`}>{card.full_name}</span>
          {card.urgent && <span className="rounded-full bg-[#C0392B] text-white text-[10px] font-bold tracking-[0.2em] px-2 py-0.5" data-testid={`card-urgent-${id}`}>URGENT</span>}
          {card.multiple && <span className="rounded-full border border-brand-ink text-brand-ink text-[10px] font-bold tracking-[0.2em] px-2 py-0.5" data-testid={`card-multiple-${id}`}>MULTIPLE #{card.multiple}</span>}
          {k === "orange" && <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#B35C12]" data-testid={`card-missed-label-${id}`}>Missed Check In</span>}
          {k === "yellow" && <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#8a6d00]" data-testid={`card-notsetup-label-${id}`}>New Not Set Up</span>}
        </div>
        <span className="text-sm text-brand-ink-muted" data-testid={`card-week-${id}`}>{k === "yellow" ? `Grace week ${card.week.label}` : `Week ${card.week.label}`}</span>
      </header>
      {card.carried_over && <p className="mt-2 text-xs text-brand-ink-muted" data-testid={`card-carried-${id}`}>Outstanding from a previous weekly cycle</p>}
      <CardDetails card={card} />
      {card.submission_id && (
        <Link to={`/coach/clients/${card.client_id}?week=${card.weekly_window_identifier}`} className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-brand-ink hover:text-[#C8965A]" data-testid={`card-full-checkin-${id}`}>
          See Full Weekly Check In <ArrowRight size={14} />
        </Link>
      )}
      <ActionNotes card={card} readOnly={completed} onValues={onValues} />
      <ReminderControl card={card} onSaved={onChanged} disabled={false} />
      {error && <p className="mt-3 text-sm text-red-700" role="alert" data-testid={`card-error-${id}`}>{error}</p>}
      <div className="mt-5 flex flex-wrap gap-3">
        {completed ? (
          <button type="button" disabled={busy} onClick={() => move("restore")} className="btn-ghost inline-flex items-center gap-2 border border-brand-border rounded-full px-4 py-2 text-sm" data-testid={`card-restore-${id}`}><RotateCcw size={14} /> Restore</button>
        ) : (
          <>
            {card.status === "live" && (
              <button type="button" disabled={busy} onClick={() => move("in_process")} className="btn-ghost inline-flex items-center gap-2 border border-brand-border rounded-full px-4 py-2 text-sm" data-testid={`card-inprocess-${id}`}><Hourglass size={14} /> In Process</button>
            )}
            <button type="button" disabled={busy} onClick={() => move("completed")} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid={`card-complete-${id}`}><CheckCircle2 size={14} /> Completed</button>
          </>
        )}
      </div>
    </article>
  );
}

export default ActionCard;
